import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import { InputField, Select, Textarea } from '@innovationnorway/component-library';
import { Button, Link } from '@innovationnorway/cl-react';

import {
  ClusterContactDto,
  ClusterContactStatus,
  ClusterContactUpdateDto,
  ClusterMemberDto,
} from 'src/api/v2';

import { requiredI18n, uniqueEmailInClusterContacts, validEmail } from 'src/utils/validation';

import InactiveNotification from 'src/features/contacts/components/InactiveNotification';
import { formatOrganizationNumber } from 'src/utils/format';
export interface ContactInformationFormProps {
  contact?: ClusterContactDto;
  members?: ClusterMemberDto[];

  onSubmit: (record: ClusterContactUpdateDto) => void;
  onCancel?: () => void;

  showContactDetailLink?: boolean;
}

const ContactInformationForm: React.FC<ContactInformationFormProps> = ({
  contact,
  members = [],

  onSubmit,
  onCancel,

  showContactDetailLink,
}) => {
  const { t: tCommon } = useTranslation();
  const { t: tError } = useTranslation('error');
  const { t: tResourceBase } = useTranslation('resourceBase');

  const isActive = useMemo(() => {
    return contact?.status === ClusterContactStatus.Active;
  }, [contact?.status]);

  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ClusterContactUpdateDto>({
    defaultValues: {
      ...contact,
      timeRateId: contact?.timeRate.id,
      clusterMemberId: contact?.clusterMemberId || '',
    },
  });

  const values = getValues();

  const sortMembers = (a: ClusterMemberDto, b: ClusterMemberDto): number => {
    if (a.name && b.name) {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'nb-NO');
    }

    return 0;
  };

  const memberToText = (value: ClusterMemberDto): string => {
    let result = '';

    if (value) {
      if (value.name) {
        result = value.name;
      }

      if (value.organizationNumber) {
        result += ` (${formatOrganizationNumber(value.organizationNumber)})`;
      }
    }

    return result;
  };

  const memberOptions = useMemo(() => {
    if (members.length > 0) {
      return [
        {
          value: '',
          text: tCommon('unspecified'),
        },
        {
          value: '',
          text: '---',
        },
        ...members.sort(sortMembers).map((member) => ({
          value: member.id,
          text: memberToText(member),
        })),
      ];
    }

    return [];
  }, [members, tCommon]);

  const cancel = () => {
    onCancel?.();

    if (!onCancel) {
      reset({
        ...contact,
        timeRateId: contact?.timeRate.id,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="display--flex flex-direction--column">
      {contact && !isActive && showContactDetailLink && <InactiveNotification clusterContact={contact} />}

      <div className="display--flex gap--2">
        <InputField
          label={tCommon('firstName')}
          {...register('firstName', {
            required: requiredI18n(tError),
          })}
          value={values.firstName}
          errorMsg={errors.firstName?.message}
          className="flex--1 margin-bottom--0"
          disabled={!isActive}
        />

        <InputField
          label={tCommon('surname')}
          {...register('lastName', {
            required: requiredI18n(tError),
          })}
          value={values.lastName}
          errorMsg={errors.lastName?.message}
          className="flex--1 margin-bottom--0"
          disabled={!isActive}
        />
      </div>

      <InputField
        label={tCommon('email')}
        {...register('emailAddress', {
          required: requiredI18n(tError),
          validate: {
            validEmail: validEmail,
            uniqueInCluster: uniqueEmailInClusterContacts(contact?.emailAddress),
          },
        })}
        value={values.emailAddress}
        errorMsg={errors.emailAddress?.message}
        className="margin-bottom--0"
        disabled={!isActive}
      />

      <InputField
        label={tCommon('mobilePhone')}
        {...register('mobilePhone')}
        value={values.mobilePhone}
        errorMsg={errors.mobilePhone?.message}
        className="margin-bottom--0"
        disabled={!isActive}
      />

      <Controller
        control={control}
        name="clusterMemberId"
        render={({ field }) => (
          <Select
            className="margin-bottom--0"
            label={tCommon('company')}
            name={field.name}
            value={field.value}
            options={memberOptions}
            onChange={(value) => {
              field.onChange(value);
            }}
            disabled={!isActive}
          />
        )}
      />

      <Textarea
        label={tCommon('jobTitle')}
        {...register('jobTitle')}
        value={values.jobTitle || undefined}
        disabled={!isActive}
      />

      <div className="display--flex justify-content--space-between">
        <div className="display--flex gap--2">
          <Button type="submit" disabled={!isActive}>
            {tCommon('save')}
          </Button>
          <Button type="button" variant="secondary" onClick={cancel} disabled={!isActive}>
            {tCommon('cancel')}
          </Button>
        </div>

        {showContactDetailLink && contact && (
          <div>
            <Link asChild>
              <RouterLink to={`/resource-base/contacts/${contact.id}`}>
                {tResourceBase('gotoContactPage')}
              </RouterLink>
            </Link>
          </div>
        )}
      </div>
    </form>
  );
};

export default ContactInformationForm;
